import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { isProfileComplete } from '@/features/profile/isProfileComplete';

/** Query key for the "does the current user have a profile row?" check. */
export const hasProfileQueryKey = (userId: string | undefined) =>
  ['has-profile', userId] as const;

/**
 * Whether the authenticated user has a USABLE profile — not merely a row.
 *
 * A social sign-in whose row was created by the old signup trigger has a
 * profile with no username, which the app cannot build an identity from. Such a
 * user is reported as incomplete so the guards send them to /complete-profile;
 * a row alone is not enough. The verdict itself lives in `isProfileComplete`,
 * shared with AuthCallback and CompleteProfile so the three cannot disagree.
 *
 * `data` is undefined while loading, then true/false. Cached indefinitely;
 * /complete-profile seeds it after creating the profile so the guards don't
 * bounce the user back.
 */
export const useHasProfile = () => {
  const { user } = useAuth();
  return useQuery({
    queryKey: hasProfileQueryKey(user?.id),
    enabled: !!user,
    staleTime: Infinity,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('id, username')
        .eq('id', user!.id)
        .maybeSingle();
      // Never swallow the error: returning false here would be cached
      // (staleTime: Infinity) and bounce a real, profiled user to
      // /complete-profile. Throw so react-query retries, and let the guards
      // fail open (render the page) if it ultimately can't be determined.
      if (error) throw error;
      return isProfileComplete({
        hasRow: !!data,
        username: data?.username ?? null,
        provider: user!.app_metadata?.provider,
      });
    },
  });
};
